import { useCallback, useEffect, useRef, useState } from 'react';

import { useOrg } from '@/contexts/OrgContext';
import { apiDelete, apiGet, apiPost } from '@/lib/http/client';

interface PushSubscription {
  id: number;
  endpoint: string;
  subscription_json: object;
  user_agent: string;
  created_at: string;
  last_used_at: string | null;
}

interface UsePushNotificationsOptions {
  enabled?: boolean;
}

export function usePushNotifications(
  options: UsePushNotificationsOptions = {}
) {
  const { enabled = true } = options;
  const { currentOrg } = useOrg();
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [subscriptions, setSubscriptions] = useState<PushSubscription[]>([]);

  // Track current enabled state and org ID in refs to detect stale async responses
  const enabledRef = useRef(enabled);
  const orgIdRef = useRef(currentOrg?.id);

  // Keep refs in sync with current values
  useEffect(() => {
    enabledRef.current = enabled;
    orgIdRef.current = currentOrg?.id;
  }, [enabled, currentOrg?.id]);

  useEffect(() => {
    // Check if running as installed PWA (standalone mode)
    // On desktop browsers, this will be false, hiding the push notification UI
    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      // iOS Safari uses a proprietary property
      (navigator as unknown as { standalone?: boolean }).standalone === true;

    const supported =
      'serviceWorker' in navigator && 'PushManager' in window && isStandalone;
    setIsSupported(supported);
  }, []);

  const loadSubscriptions = useCallback(async () => {
    if (!enabled || !currentOrg?.id) {
      setIsLoading(false);
      setIsSubscribed(false);
      setSubscriptions([]);
      return;
    }
    const orgId = currentOrg.id;
    setIsLoading(true);
    try {
      const response = await apiGet<PushSubscription[]>(
        `/api/orgs/${orgId}/push-subscriptions/`
      );
      // Guard against stale responses: check current ref values, not closure-captured values
      if (!enabledRef.current || orgId !== orgIdRef.current) {
        return;
      }
      setSubscriptions(response);
      setIsSubscribed(response.length > 0);
    } catch (error) {
      console.error('Failed to load push subscriptions:', error);
    } finally {
      // Only update loading state if this request is still relevant
      if (enabledRef.current && orgId === orgIdRef.current) {
        setIsLoading(false);
      }
    }
  }, [enabled, currentOrg?.id]);

  useEffect(() => {
    loadSubscriptions();
  }, [loadSubscriptions]);

  const subscribe = useCallback(async () => {
    if (!currentOrg?.id || !isSupported) {
      return false;
    }
    try {
      const { vapid_public_key } = await apiGet<{ vapid_public_key: string }>(
        `/api/orgs/${currentOrg.id}/push-subscriptions/vapid-public-key/`
      );
      const registration = await navigator.serviceWorker.register('/sw.js');
      await navigator.serviceWorker.ready;
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        return false;
      }
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapid_public_key),
      });
      await apiPost(`/api/orgs/${currentOrg.id}/push-subscriptions/`, {
        subscription: subscription.toJSON(),
        user_agent: navigator.userAgent,
      });
      await loadSubscriptions();
      return true;
    } catch (error) {
      console.error('Failed to subscribe to push notifications:', error);
      return false;
    }
  }, [currentOrg?.id, isSupported, loadSubscriptions]);

  const unsubscribe = useCallback(async () => {
    if (!currentOrg?.id) {
      return false;
    }
    try {
      for (const sub of subscriptions) {
        await apiDelete(
          `/api/orgs/${currentOrg.id}/push-subscriptions/${sub.id}/`
        );
      }
      await loadSubscriptions();
      return true;
    } catch (error) {
      console.error('Failed to unsubscribe from push notifications:', error);
      return false;
    }
  }, [currentOrg?.id, subscriptions, loadSubscriptions]);

  return {
    isSupported,
    isSubscribed,
    isLoading,
    subscriptions,
    subscribe,
    unsubscribe,
  };
}

function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const buffer = new ArrayBuffer(rawData.length);
  const outputArray = new Uint8Array(buffer);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}
